import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Select, Input, message } from "antd";
import { ApolloError } from "@apollo/client";
import { Lock, Check } from "lucide-react";

import OllieLayout from "components/layouts/ollie-layout/ollie-layout";
import { useSubmitSalesContactRequestMutation } from "graphql/mutations/submit-sales-contact-request.generated";
import routeNames from "constants/route-names";
import { SALES_CONTACT_PROJECT_ROLE_OPTIONS, SALES_CONTACT_TOTAL_SIZE_OPTIONS } from "constants/constants";
import { BUILDING_TYPE_OPTIONS } from "constants/property-field-options";

enum GateStep {
	CTA = "cta",
	FORM = "form",
	CONFIRMATION = "confirmation"
}

const WHATS_INCLUDED = [
	"Priority scheduling",
	"Mobile field inspections",
	"Real-time code analysis",
	"Automated punch list generation",
	"Unlimited photo & video storage"
];

// Gates a user's ability to create a new Ollie inspection behind a "Contact Sales" CTA
// (MWA-836), a stand-in for self-pay billing until that ships for inspections the way
// it already has for plan reviews.
const OllieInspectionGate = () => {
	const navigate = useNavigate();
	const [step, setStep] = useState<GateStep>(GateStep.CTA);

	const [projectRole, setProjectRole] = useState<string>();
	const [propertyType, setPropertyType] = useState<string>();
	const [totalSize, setTotalSize] = useState<string>();
	const [location, setLocation] = useState("");
	const [notes, setNotes] = useState("");

	const goToDashboard = useCallback(() => navigate(routeNames.OLLIE_DASHBOARD), [navigate]);

	const [submitSalesContactRequestMutation, { loading: isSubmitting }] = useSubmitSalesContactRequestMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only"
	});

	const canSubmit = !!projectRole && !!propertyType && !!totalSize && !!location.trim();

	const handleSubmit = useCallback(async () => {
		if (!canSubmit) return;
		try {
			await submitSalesContactRequestMutation({
				variables: {
					project_role: projectRole as string,
					property_type: propertyType as string,
					total_size: totalSize as string,
					location: location.trim(),
					notes: notes.trim() || null
				}
			});
			setStep(GateStep.CONFIRMATION);
		} catch (error) {
			const serverMessage = (error as ApolloError).graphQLErrors?.[0]?.message;
			message.error(serverMessage || "Your request could not be sent. Please try again.");
		}
	}, [canSubmit, submitSalesContactRequestMutation, projectRole, propertyType, totalSize, location, notes]);

	const renderCta = () => (
		<div className="oig-cta">
			<div className="oig-icon-badge">
				<span className="oig-premium-badge">Premium</span>
				<Lock className="oig-icon-lock" />
			</div>
			<h2 className="oig-title">Unlock Site Inspections</h2>
			<p className="oig-subtitle">
				Take Ollie into the field. Increase your first-time pass rate and qualify for priority Fast Lane
				scheduling. Get instant readiness reports, recommended fixes, and comprehensive code analysis.
			</p>
			<div className="oig-cta-actions">
				<button type="button" className="oig-btn-primary" onClick={() => setStep(GateStep.FORM)}>
					Contact Sales to Upgrade
				</button>
				<button type="button" className="oig-btn-secondary" onClick={goToDashboard}>
					Back to Dashboard
				</button>
			</div>
			<div className="oig-included">
				<h4 className="oig-included-title">What&apos;s Included</h4>
				<ul className="oig-included-list">
					{WHATS_INCLUDED.map(item => (
						<li key={item} className="oig-included-item">
							<span className="oig-included-check">
								<Check className="oig-included-check-icon" />
							</span>
							{item}
						</li>
					))}
				</ul>
			</div>
		</div>
	);

	const renderForm = () => (
		<div className="oig-form">
			<div className="oig-form-header">
				<h3 className="oig-form-title">Tell us a bit more about the project</h3>
			</div>
			<div className="oig-form-body">
				<div className="oig-field">
					<label className="oig-field-label">Project Role</label>
					<Select
						className="oig-select"
						placeholder="Select your role..."
						value={projectRole}
						onChange={setProjectRole}
						options={[...SALES_CONTACT_PROJECT_ROLE_OPTIONS]}
						disabled={isSubmitting}
					/>
				</div>
				<div className="oig-field">
					<label className="oig-field-label">Property Type</label>
					<Select
						className="oig-select"
						placeholder="Select property type..."
						value={propertyType}
						onChange={setPropertyType}
						options={BUILDING_TYPE_OPTIONS}
						disabled={isSubmitting}
					/>
				</div>
				<div className="oig-field">
					<label className="oig-field-label">Total Size</label>
					<Select
						className="oig-select"
						placeholder="Select total size..."
						value={totalSize}
						onChange={setTotalSize}
						options={[...SALES_CONTACT_TOTAL_SIZE_OPTIONS]}
						disabled={isSubmitting}
					/>
				</div>
				<div className="oig-field">
					<label className="oig-field-label">Location (City, State)</label>
					<Input
						className="oig-input"
						placeholder="e.g. Austin, TX so we can confirm jurisdiction"
						value={location}
						onChange={e => setLocation(e.target.value)}
						disabled={isSubmitting}
					/>
				</div>
				<div className="oig-field">
					<label className="oig-field-label">Notes (Optional)</label>
					<Input.TextArea
						className="oig-textarea"
						placeholder="Any specific requirements or questions?"
						value={notes}
						onChange={e => setNotes(e.target.value)}
						autoSize={{ minRows: 3, maxRows: 6 }}
						disabled={isSubmitting}
					/>
				</div>
			</div>
			<div className="oig-form-footer">
				<button
					type="button"
					className="oig-btn-primary oig-submit-btn"
					disabled={!canSubmit || isSubmitting}
					onClick={handleSubmit}
				>
					Submit Request
				</button>
			</div>
		</div>
	);

	const renderConfirmation = () => (
		<div className="oig-confirmation">
			<span className="oig-confirmation-icon">
				<Check className="oig-confirmation-check-icon" />
			</span>
			<h3 className="oig-title">Request Sent</h3>
			<p className="oig-confirmation-text">
				Thanks for reaching out! Our team will be in touch shortly to help you get started with Ollie
				Inspections.
			</p>
			<button type="button" className="oig-btn-secondary oig-confirmation-btn" onClick={goToDashboard}>
				Back to Dashboard
			</button>
		</div>
	);

	return (
		<OllieLayout>
			<div className="oig-page">
				<Modal
					open
					footer={null}
					title={null}
					closable={false}
					maskClosable={false}
					centered
					width={step === GateStep.CTA ? 578 : 448}
					className={`oig-modal ${step === GateStep.CTA ? "oig-modal-cta" : ""}`}
				>
					{step === GateStep.CTA && renderCta()}
					{step === GateStep.FORM && renderForm()}
					{step === GateStep.CONFIRMATION && renderConfirmation()}
				</Modal>
			</div>
		</OllieLayout>
	);
};

export default OllieInspectionGate;
